import { Check, Clock, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PortOrderStatus } from '../types';

// Timeline steps matching the mockup
export interface TimelineStep {
  id: string;
  label: string;
  description?: string;
  completedDate?: string | null;
  isCurrentStep?: boolean;
}

// Default port timeline steps
const defaultSteps: TimelineStep[] = [
  { id: 'submitted', label: 'Request Submitted', description: 'Port order received' },
  { id: 'csr-verified', label: 'CSR Verified', description: 'Customer service record validated' },
  { id: 'carrier-approval', label: 'Losing Carrier Approval', description: 'Previous carrier has approved' },
  { id: 'foc-received', label: 'FOC Received', description: 'Firm order commitment date set' },
  { id: 'activation', label: 'Number Activation', description: 'Numbers being activated' },
  { id: 'complete', label: 'Port Complete', description: 'Numbers are now active' },
];

// Map port order status to timeline step index
function getStepIndex(status: PortOrderStatus | null): number {
  switch (status) {
    case 'NEW':
      return 0;
    case 'PENDING':
      return 1;
    case 'SUBMITTED':
      return 2;
    case 'FOCd':
      return 3;
    case 'COMPLETED':
      return 5; // All steps complete
    case 'CANCELLED':
    case 'REJECTED':
      return -1; // Terminal failed state
    default:
      return 0;
  }
}

interface PortTimelineProps {
  status: PortOrderStatus | null;
  submittedDate?: string | null;
  focDate?: string | null;
  completedDate?: string | null;
  className?: string;
  variant?: 'vertical' | 'horizontal';
}

export function PortTimeline({
  status,
  submittedDate,
  focDate,
  completedDate,
  className,
  variant = 'vertical',
}: PortTimelineProps) {
  const currentStepIndex = getStepIndex(status);
  const isTerminal = status === 'CANCELLED' || status === 'REJECTED';

  // Build steps with dates
  const steps: TimelineStep[] = defaultSteps.map((step, index) => {
    let stepCompletedDate: string | null = null;

    // Add dates to specific steps
    if (step.id === 'submitted' && submittedDate) {
      stepCompletedDate = submittedDate;
    } else if (step.id === 'foc-received' && focDate) {
      stepCompletedDate = focDate;
    } else if (step.id === 'complete' && completedDate) {
      stepCompletedDate = completedDate;
    }

    return {
      ...step,
      completedDate: stepCompletedDate,
      isCurrentStep: index === currentStepIndex && !isTerminal,
    };
  });

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  if (variant === 'horizontal') {
    return (
      <div className={cn('flex items-center justify-between', className)}>
        {steps.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = step.isCurrentStep;
          const isUpcoming = index > currentStepIndex;

          return (
            <div key={step.id} className="flex-1 flex items-center">
              {/* Step indicator */}
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center transition-all',
                    isCompleted && 'bg-cyan-500 text-white',
                    isCurrent && 'bg-indigo-500 text-white animate-pulse',
                    isUpcoming && 'bg-slate-200 text-slate-400',
                    isTerminal && index === 0 && 'bg-red-500 text-white'
                  )}
                >
                  {isCompleted ? (
                    <Check size={16} />
                  ) : isCurrent ? (
                    <Clock size={16} />
                  ) : (
                    <Circle size={16} />
                  )}
                </div>
                <span
                  className={cn(
                    'text-xs mt-1 text-center max-w-[80px]',
                    isCompleted && 'text-cyan-600 font-medium',
                    isCurrent && 'text-indigo-600 font-medium',
                    isUpcoming && 'text-slate-400'
                  )}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-2',
                    index < currentStepIndex ? 'bg-cyan-500' : 'bg-slate-200'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Vertical variant (default)
  return (
    <div className={cn('relative', className)}>
      {/* Vertical line */}
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />

      {steps.map((step, index) => {
        const isCompleted = index < currentStepIndex;
        const isCurrent = step.isCurrentStep;
        const isUpcoming = index > currentStepIndex;
        const showFOCCard = step.id === 'foc-received' && focDate && (isCompleted || isCurrent);

        return (
          <div key={step.id} className="relative flex items-start mb-6 last:mb-0">
            {/* Step indicator */}
            <div
              className={cn(
                'relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all',
                isCompleted && 'bg-cyan-500 text-white',
                isCurrent && 'bg-indigo-500 text-white ring-4 ring-indigo-100',
                isUpcoming && 'bg-white border-2 border-slate-200 text-slate-400',
                isTerminal && index === 0 && 'bg-red-500 text-white'
              )}
            >
              {isCompleted ? (
                <Check size={16} />
              ) : isCurrent ? (
                <Clock size={16} className="animate-pulse" />
              ) : (
                <Circle size={14} />
              )}
            </div>

            {/* Step content */}
            <div className="ml-4 flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4
                  className={cn(
                    'font-medium',
                    isCompleted && 'text-slate-900',
                    isCurrent && 'text-indigo-600',
                    isUpcoming && 'text-slate-400'
                  )}
                >
                  {step.label}
                </h4>
                {step.completedDate && isCompleted && (
                  <span className="text-xs text-slate-400">
                    {formatDate(step.completedDate)}
                  </span>
                )}
              </div>
              <p
                className={cn(
                  'text-sm mt-0.5',
                  isCompleted || isCurrent ? 'text-slate-500' : 'text-slate-300'
                )}
              >
                {step.description}
              </p>

              {/* FOC Date Card - highlighted */}
              {showFOCCard && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                  <div className="text-xs text-indigo-600 font-medium">
                    Scheduled: {formatDate(focDate)}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Terminal status message */}
      {isTerminal && (
        <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
          <p className="text-sm text-red-600 font-medium">
            {status === 'REJECTED'
              ? 'Port order was rejected by the losing carrier.'
              : 'Port order was cancelled.'}
          </p>
        </div>
      )}
    </div>
  );
}
